import { useDialogContext } from '@ark-ui/react';
import { Amount } from '@ui/components/Amount/Amount';
import { AmountUsd } from '@ui/components/Amount/AmountUsd';
import { X } from '@ui/components/icons';
import { LongShortBadge } from '@ui/components/LongShortBadge';
import { StatLabel } from '@ui/components/Stats';
import { RequireConnectionClickable } from '@ui/features/Wallet/components/RequireConnectionClickable';
import { useNativeToken } from '@ui/hooks/useNativeToken';
import { Portal } from '@ui/lib/park/portal';
import { Button } from '@ui/lib/park/ui/button';
import { Dialog } from '@ui/lib/park/ui/dialog';
import { Icon } from '@ui/lib/park/ui/icon';
import { Text } from '@ui/lib/park/ui/text';
import { Position } from '@ui/services/contract/hooks/TradingReaderFacet/useGetPositionV2/types';
import BigNumber from 'bignumber.js';
import { isNil } from 'lodash-es';
import { useTranslation } from 'next-i18next';
import { useState } from 'react';
import { Grid, HStack, Stack, styled } from 'styled-system/jsx';

import { useInvalidatePositionQuery } from '../../hooks/useInvalidateQuery';
import {
  CollateralChange as AddCollateralChange,
  CollateralRatioChange as AddCollateralRatioChange,
  LeverageChange as AddLeverageChange,
  LiquidationPriceChange as AddLiquidationPriceChange,
} from '../AddCollateral/changes';
import { DepositCollateralInput } from '../AddCollateral/DepositCollateralInput';
import { useDepositCollateral } from '../AddCollateral/hooks/useDepositCollateral';
import { useDepositCollateralGasFee } from '../AddCollateral/hooks/useDepositCollateralGasFee';
import { useDepositCollateralInput } from '../AddCollateral/hooks/useDepositCollateralInput';
import { valueStyle } from '../AddCollateral/styles';
import {
  AddRemoveButtonGroup,
  MarginTab,
} from '../AddRemoveButtonGroup';
import { TradeLeverageBadge } from '../LeverageBadge';
import { MarketPrice } from '../PositionList/MarketPrice';
import { useRemoveMarginOracleData } from '../RemoveCollateral/hooks/useRemoveMarginOracleData';
import { useWithdrawCollateral } from '../RemoveCollateral/hooks/useWithdrawCollateral';
import { useWithdrawCollateralGasFee } from '../RemoveCollateral/hooks/useWithdrawCollateralGasFee';
import { useWithdrawCollateralInput } from '../RemoveCollateral/hooks/useWithdrawCollateralInput';
import { useWithdrawCollateralValidation } from '../RemoveCollateral/hooks/useWithdrawCollateralValidation';
import {
  CollateralChange as RemoveCollateralChange,
  CollateralRatioChange as RemoveCollateralRatioChange,
  LeverageChange as RemoveLeverageChange,
  LiquidationPriceChange as RemoveLiquidationPriceChange,
  TakeProfitChange as RemoveTakeProfitChange,
} from '../RemoveCollateral/removeChanges';
import { WithdrawCollateralInput } from '../RemoveCollateral/WithdrawCollateralInput';
import { SizeUsd } from '../SizeUsd';
import { useCollateralToken } from '../TradeModule/store/useCollateralToken';

const resetMarginInputs = () => {
  useDepositCollateralInput.getState().reset();
  useWithdrawCollateralInput.getState().reset();
};

export const MarginOverlay = ({
  children,
  position,
  ...rest
}: Dialog.RootProps & {
  position: Position;
}) => {
  const { t } = useTranslation();
  const [tab, setTab] = useState(MarginTab.Add);

  return (
    <Dialog.Root
      lazyMount
      unmountOnExit
      {...rest}
      onOpenChange={(details) => {
        if (!details.open) {
          resetMarginInputs();
        }
        rest.onOpenChange?.(details);
      }}
    >
      <Dialog.Trigger asChild>{children}</Dialog.Trigger>
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content minWidth="400px" data-cy="MarginModalContent">
            <Stack gap="4" p="6" pb={0}>
              <HStack justifyContent="space-between">
                <Dialog.Title p={0}>
                  {t('common.Manage Collateral')}
                </Dialog.Title>
                <Dialog.CloseTrigger boxShadow="none" bg="none">
                  <Icon w="20px" h="20px">
                    <X />
                  </Icon>
                </Dialog.CloseTrigger>
              </HStack>
              <Dialog.Description>
                <HStack
                  p="4"
                  borderRadius="8px"
                  gap={1}
                  w="full"
                  bg="black.lv1"
                >
                  <styled.img
                    src={position.pair.image}
                    boxSize="4"
                    borderRadius="6px"
                  />
                  <Text textStyle="headings.default">
                    {position.pair.baseName}
                  </Text>
                  <LongShortBadge isLong={position.isLong} />
                  <TradeLeverageBadge genericTradeParams={position} />
                </HStack>
              </Dialog.Description>
              <AddRemoveButtonGroup tab={tab} setTab={setTab} w="full" />
            </Stack>

            {tab === MarginTab.Add ? (
              <AddCollateralPanel position={position} />
            ) : (
              <RemoveCollateralPanel position={position} />
            )}
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

const AddCollateralPanel = ({ position }: { position: Position }) => {
  const { t } = useTranslation();

  return (
    <>
      <Stack gap={1} p="6" pt={2} borderTopWidth={0}>
        <DepositCollateralInput position={position} />
        <MarginStatsGrid position={position} mode="add" />
      </Stack>
      <Stack flexDir="column" gap={4} p="6" pt={0} borderTop="none">
        <HStack p="2" justifyContent="space-between">
          <StatLabel>{t('common.Estimated Gas Fee')}</StatLabel>
          <AddEstimatedGasFee position={position} />
        </HStack>
        <AddCollateralButton position={position} />
      </Stack>
    </>
  );
};

const RemoveCollateralPanel = ({ position }: { position: Position }) => {
  const { t } = useTranslation();

  return (
    <>
      <Stack gap={1} p="6" pt={2} borderTopWidth={0}>
        <WithdrawCollateralInput position={position} />
        <MarginStatsGrid position={position} mode="remove" />
      </Stack>
      <Stack flexDir="column" gap={4} p="6" pt={0} borderTop="none">
        <HStack p="2" justifyContent="space-between">
          <StatLabel>{t('common.Estimated Gas Fee')}</StatLabel>
          <RemoveEstimatedGasFee position={position} />
        </HStack>
        <RemoveCollateralButton position={position} />
      </Stack>
    </>
  );
};

const MarginStatsGrid = ({
  position,
  mode,
}: {
  position: Position;
  mode: 'add' | 'remove';
}) => {
  const { t } = useTranslation();
  const isAdd = mode === 'add';

  return (
    <Grid
      p={3}
      borderRadius={4}
      bg="greyWhite.darkGary3"
      gridTemplateColumns="1fr auto"
    >
      <StatLabel>{t('common.Collateral')}</StatLabel>
      {isAdd ? (
        <AddCollateralChange position={position} />
      ) : (
        <RemoveCollateralChange position={position} />
      )}

      <StatLabel>{t('common.Collateral Ratio')}</StatLabel>
      {isAdd ? (
        <AddCollateralRatioChange position={position} />
      ) : (
        <RemoveCollateralRatioChange position={position} />
      )}

      <StatLabel>{t('common.Leverage')}</StatLabel>
      {isAdd ? (
        <AddLeverageChange position={position} />
      ) : (
        <RemoveLeverageChange position={position} />
      )}

      <StatLabel>{t('common.Size')}</StatLabel>
      <SizeUsd genericTradeParams={position} css={valueStyle} />
      <StatLabel>{t('common.Mark Price')}</StatLabel>
      <MarketPrice position={position} css={valueStyle} />

      {!isAdd && (
        <>
          <StatLabel>{t('common.Take Profit')}</StatLabel>
          <RemoveTakeProfitChange position={position} />
        </>
      )}

      <StatLabel>{t('common.Liquidation Price')}</StatLabel>
      {isAdd ? (
        <AddLiquidationPriceChange position={position} />
      ) : (
        <RemoveLiquidationPriceChange position={position} />
      )}
    </Grid>
  );
};

const AddEstimatedGasFee = ({ position }: { position: Position }) => {
  const { data: gasFee } = useDepositCollateralGasFee(position);
  const { symbol } = useNativeToken();

  return (
    <GasFeeDisplay gasFee={gasFee} symbol={symbol} dataCyPrefix="Add" />
  );
};

const RemoveEstimatedGasFee = ({ position }: { position: Position }) => {
  const { data: gasFee } = useWithdrawCollateralGasFee(position);
  const { symbol } = useNativeToken();

  return (
    <GasFeeDisplay gasFee={gasFee} symbol={symbol} dataCyPrefix="Remove" />
  );
};

const GasFeeDisplay = ({
  gasFee,
  symbol,
  dataCyPrefix,
}: {
  gasFee?: {
    gasInNativeToken?: BigNumber;
    gasInUsd?: BigNumber;
  };
  symbol: string;
  dataCyPrefix: string;
}) => (
  <HStack gap={1}>
    <Amount
      css={valueStyle}
      value={gasFee?.gasInNativeToken}
      opts={{
        suffix: ' ' + symbol,
      }}
      data-cy={`${dataCyPrefix}EstimatedGasFeeInNativeToken`}
      data-cy-value={gasFee?.gasInNativeToken}
    />
    <AmountUsd
      value={gasFee?.gasInUsd}
      css={valueStyle}
      opts={{
        prefix: '($',
        suffix: ')',
      }}
      data-cy={`${dataCyPrefix}EstimatedGasFeeInUsd`}
      data-cy-value={gasFee?.gasInUsd}
    />
  </HStack>
);

const AddCollateralButton = ({ position }: { position: Position }) => {
  const { t } = useTranslation();
  const dialogCtx = useDialogContext();
  const invalidatePositionQuery = useInvalidatePositionQuery();

  const { writeContract, isPending } = useDepositCollateral({
    onSuccess() {
      dialogCtx.setOpen(false);
      resetMarginInputs();
      invalidatePositionQuery();
    },
  });
  const tokenAmount = useDepositCollateralInput((s) => s.input.value.token);
  const useValidation = useDepositCollateralInput((s) => s.useValidation);

  const payToken =
    useCollateralToken((s) => s.useCollateralTokenValue()) ??
    position.collateralToken;
  const validation = useValidation({
    position: {
      ...position,
      collateralToken: payToken,
    },
  });

  return (
    <RequireConnectionClickable>
      <Button
        width="full"
        variant="primary"
        size="big"
        disabled={!!validation.error || isNil(tokenAmount)}
        loading={isPending}
        data-cy="AddCollateralSubmitButton"
        onClick={() => {
          if (!tokenAmount) {
            throw new Error('button should not be enabled');
          }

          writeContract({
            amount: tokenAmount,
            payToken: payToken,
            tradeHash: position.id,
          });
        }}
      >
        {t('common.Add Collateral')}
      </Button>
    </RequireConnectionClickable>
  );
};

const RemoveCollateralButton = ({ position }: { position: Position }) => {
  const { t } = useTranslation();
  const dialogCtx = useDialogContext();
  const invalidatePositionQuery = useInvalidatePositionQuery();

  const { writeContract, isPending } = useWithdrawCollateral({
    onSuccess() {
      dialogCtx.setOpen(false);
      resetMarginInputs();
      invalidatePositionQuery();
    },
  });
  const tokenAmount = useWithdrawCollateralInput((s) => s.input.value.token);
  const validation = useWithdrawCollateralValidation({ position });
  const oracleData = useRemoveMarginOracleData(position);

  return (
    <RequireConnectionClickable>
      <Button
        width="full"
        variant="primary"
        size="big"
        disabled={!!validation.error || isNil(tokenAmount) || isNil(oracleData)}
        loading={isPending}
        data-cy="RemoveCollateralSubmitButton"
        onClick={() => {
          if (!tokenAmount || !oracleData) {
            throw new Error('button should not be enabled');
          }

          writeContract({
            amount: tokenAmount,
            payToken: position.collateralToken,
            tradeHash: position.id,
            oracleData,
          });
        }}
      >
        {t('common.Remove Collateral')}
      </Button>
    </RequireConnectionClickable>
  );
};

/** @deprecated Use MarginOverlay */
export const AddCollateralOverlay = MarginOverlay;
